import type { IsoKey } from "./illustrations";

/** One "use case" card: a concrete scenario the platform covers for an industry. */
export type IndustryUseCase = {
  title: string;
  body: string;
  points: string[];
  iso: IsoKey;
  /** Optional deep link to the module or add-on that delivers it. */
  link?: { label: string; to: string };
};

export type IndustryUseCaseSet = {
  eyebrow: string;
  title: string;
  lead: string;
  cases: IndustryUseCase[];
};

const DEFAULT_HEADING = {
  eyebrow: "Use cases",
  title: "How teams run this industry on factory.online",
  lead: "Concrete scenarios our customers automate first — from the quote to the shipped pallet.",
};

export const INDUSTRY_USE_CASES: Record<string, IndustryUseCaseSet> = {
  "cable-wire-manufacturing": {
    ...DEFAULT_HEADING,
    title: "Use cases for cable & wire plants",
    cases: [
      {
        title: "Quote a custom cable in minutes",
        body: "Build the construction from standards, price copper by the day's LME rate and send a technically valid offer before the competitor calls back.",
        points: ["Cross-section designer", "Live metal surcharge", "Margin check per offer"],
        iso: "indCableQuote",
        link: { label: "CablePLM add-on", to: "/addons/cable-plm" },
      },
      {
        title: "Drum and length traceability",
        body: "Every drum keeps its length, batch and test record, so partial deliveries and returns never break the stock picture.",
        points: ["Drum-level stock", "Remnant reuse", "Test certificates on hand"],
        iso: "indCableDrum",
        link: { label: "Stocks module", to: "/stocks" },
      },
      {
        title: "Extrusion line scheduling",
        body: "Sequence orders by die and compound to cut changeovers, and see instantly what a rush order costs the rest of the week.",
        points: ["Changeover-aware sequencing", "Line capacity view", "Rush-order impact"],
        iso: "indCableExtrusion",
        link: { label: "Scheduling module", to: "/scheduling" },
      },
      {
        title: "Copper cost and real margin",
        body: "Actual consumption, scrap and energy roll into the order cost, so you know the margin on every reel — not on the average.",
        points: ["Scrap accounting", "Order-level P&L", "Price list recalculation"],
        iso: "indCableCost",
        link: { label: "Cost & margins", to: "/cost" },
      },
    ],
  },
  "fiber-optic-cables": {
    ...DEFAULT_HEADING,
    title: "Use cases for fiber optic cable plants",
    cases: [
      {
        title: "Fixed fiber batches, minimal waste",
        body: "Fiber comes in pre-set lengths. Allocate batches to drum orders so offcuts and mid-run splices shrink to a minimum.",
        points: ["Batch-to-drum allocation", "Splice point tracking", "Scrap per construction"],
        iso: "indFiberBatches",
        link: { label: "Stocks module", to: "/stocks" },
      },
      {
        title: "Coloring and fiber sequence control",
        body: "Coloring lines carry the color code and tube/module map on the work order, so sequence follows TIA-598 or the customer spec — not memory.",
        points: ["Coloring operations", "Ring marking", "Tube & module maps"],
        iso: "indFiberColoring",
        link: { label: "Production module", to: "/production" },
      },
      {
        title: "Per-drum optical test records",
        body: "OTDR attenuation results travel with the drum and its batch genealogy, so a failed test shows the full rework scope in one click.",
        points: ["Attenuation per drum", "Test certificates on hand", "Forward/backward trace"],
        iso: "indFiberOtdr",
        link: { label: "Technical data", to: "/technical-data" },
      },
      {
        title: "Stranding and sheathing scheduling",
        body: "Sequence SZ-stranding and extrusion by construction family to cut setup scrap on the constraint lines and see rush-order impact instantly.",
        points: ["Construction families", "Line capacity view", "Changeover reduction"],
        iso: "indFiberStranding",
        link: { label: "Scheduling module", to: "/scheduling" },
      },
    ],
  },
  "metals-mechanical-engineering": {
    ...DEFAULT_HEADING,
    title: "Use cases for assembly & miltech",
    cases: [
      {
        title: "Engineering changes under control",
        body: "Releases start from the approved revision, so no batch is ever built to an outdated drawing or an old firmware version.",
        points: ["Revision control", "Change notices", "Shop-floor document access"],
        iso: "indMetalsDrawing",
        link: { label: "PDM add-on", to: "/addons/pdm" },
      },
      {
        title: "One picture across distributed sites",
        body: "Stock, transit and work status from every remote site sit in one view instead of spreadsheets and messenger threads.",
        points: ["Multi-site stock", "Inter-site transfers", "Role-based access"],
        iso: "indMetalsNesting",
        link: { label: "Stocks module", to: "/stocks" },
      },
      {
        title: "Serial-number traceability",
        body: "Every component is traced from supplier receipt through assembly and test to handover, including subcontract stages.",
        points: ["Serial and lot records", "Subcontract stages", "Test and QC history"],
        iso: "indMetalsRouting",
        link: { label: "Production module", to: "/production" },
      },
      {
        title: "Kit completeness and realistic dates",
        body: "MRP checks every site before the line starts and finite-capacity planning gives a date the shop can actually hold as volumes multiply.",
        points: ["Kit shortage alerts", "Finite capacity", "Promise dates"],
        iso: "indMetalsLoad",
        link: { label: "Planning module", to: "/planning" },
      },
    ],
  },
  "aluminum-extrusion": {
    ...DEFAULT_HEADING,
    title: "Use cases for aluminum & extrusion",
    cases: [
      {
        title: "Die library and campaign planning",
        body: "Group orders by die, alloy and billet to run longer campaigns with fewer press stops.",
        points: ["Die master data", "Campaign grouping", "Press utilisation"],
        iso: "indAluDie",
        link: { label: "Scheduling module", to: "/scheduling" },
      },
      {
        title: "Billet and scrap balance",
        body: "Reconcile billet input against profile output, butts and scrap in one recovery figure per run.",
        points: ["Recovery rate", "Scrap loops", "Alloy traceability"],
        iso: "indAluBillet",
        link: { label: "Cost & margins", to: "/cost" },
      },
      {
        title: "Finishing and anodising flow",
        body: "Anodising, powder coating and packing are planned as their own stages with queues and capacity.",
        points: ["Stage queues", "Colour batching", "Quality holds"],
        iso: "indAluAnodising",
        link: { label: "Production module", to: "/production" },
      },
      {
        title: "Long-profile warehousing",
        body: "Rack and pick long profiles without damage using length-aware storage and handling units.",
        points: ["Length-aware slots", "Handling units", "Mobile picking"],
        iso: "indAluRacking",
        link: { label: "WMS add-on", to: "/addons/wms" },
      },
    ],
  },
  "food-feed-production": {
    ...DEFAULT_HEADING,
    title: "Use cases for food & feed production",
    cases: [
      {
        title: "Recipe versions and allergens",
        body: "Formulations are versioned with allergen and nutrition data that flow straight to the label.",
        points: ["Recipe versioning", "Allergen matrix", "Label data"],
        iso: "indFoodRecipe",
        link: { label: "Technical data", to: "/technical-data" },
      },
      {
        title: "Batch and shelf-life traceability",
        body: "One click gives the full genealogy from raw batch to shipped pallet — and back — for any recall drill.",
        points: ["Forward/backward trace", "Best-before control", "FEFO picking"],
        iso: "indFoodTrace",
        link: { label: "Stocks module", to: "/stocks" },
      },
      {
        title: "Yield, shrink and giveaway",
        body: "Compare planned versus actual yield per batch to see where product weight is quietly lost.",
        points: ["Batch yield", "Giveaway control", "Cost per kg"],
        iso: "indFoodYield",
        link: { label: "Cost & margins", to: "/cost" },
      },
      {
        title: "Demand-driven raw purchasing",
        body: "Perishable inputs are ordered against the plan, not against a guess, keeping fresh stock lean.",
        points: ["Requirement runs", "Supplier lead times", "Shelf-life aware orders"],
        iso: "indFoodPurchasing",
        link: { label: "Purchases module", to: "/purchases" },
      },
    ],
  },
  "chemical-process-manufacturing": {
    ...DEFAULT_HEADING,
    title: "Use cases for chemical & process manufacturing",
    cases: [
      {
        title: "Formulation with concentration math",
        body: "Handle active-ingredient strength, density and unit conversions inside the recipe instead of in spreadsheets.",
        points: ["Potency-based BOM", "Unit conversions", "Automatic rebalancing"],
        iso: "indChemFormulation",
        link: { label: "Technical data", to: "/technical-data" },
      },
      {
        title: "Lot quality and CoA",
        body: "Quality checks gate the release of each lot, and the certificate of analysis is generated from the results.",
        points: ["Sampling plans", "Release gates", "CoA output"],
        iso: "indChemCoa",
        link: { label: "Production module", to: "/production" },
      },
      {
        title: "Tanks, vessels and campaigns",
        body: "Plan around vessel availability and cleaning time, not just labour hours.",
        points: ["Vessel capacity", "CIP windows", "Campaign sequencing"],
        iso: "indChemVessels",
        link: { label: "Scheduling module", to: "/scheduling" },
      },
      {
        title: "Hazard data and packaging",
        body: "Store safety classifications with the item so shipping documents and packaging rules follow automatically.",
        points: ["Safety data", "Packaging rules", "Document pack"],
        iso: "indChemHazard",
        link: { label: "WMS add-on", to: "/addons/wms" },
      },
    ],
  },
  "jobshop-custom-production": {
    ...DEFAULT_HEADING,
    title: "Use cases for jobshops & custom production",
    cases: [
      {
        title: "Estimate a one-off job fast",
        body: "Reuse similar past jobs to price a new enquiry with real cost data instead of gut feeling.",
        points: ["Estimate from history", "Cost build-up", "Quote versions"],
        iso: "indJobshopEstimate",
        link: { label: "CRM add-on", to: "/addons/crm" },
      },
      {
        title: "Engineer-to-order routing",
        body: "Create BOM and route while the job runs, with changes tracked instead of shouted across the shop.",
        points: ["On-the-fly BOM", "Change history", "Progress per operation"],
        iso: "indJobshopEto",
        link: { label: "Project Management add-on", to: "/addons/project-management" },
      },
      {
        title: "Shop-floor time and material capture",
        body: "Operators book time and material at the terminal, so job cost is current, not month-end news.",
        points: ["Terminal booking", "Live job cost", "Overrun alerts"],
        iso: "indJobshopTerminal",
        link: { label: "Production module", to: "/production" },
      },
      {
        title: "Aftersales and service jobs",
        body: "Repairs, warranty and field work run as their own orders against the delivered equipment.",
        points: ["Service orders", "Equipment history", "Field task assignment"],
        iso: "indJobshopService",
        link: { label: "Services add-on", to: "/addons/services" },
      },
    ],
  },
};

/** Use-case set for an industry slug, if we have curated one. */
export function industryUseCases(slug: string): IndustryUseCaseSet | null {
  return INDUSTRY_USE_CASES[slug] ?? null;
}
